import Newsletter from "../components/Newsletter";
import useCanonical, { SITE } from "../lib/useCanonical";
import usePageMeta from "../lib/usePageMeta";

const ABOUT_TITLE = "About The Tea List — A Sri Lankan family's tea journal, made in Melbourne";
const ABOUT_DESCRIPTION =
  "Meet the family behind The Tea List: Ceylon-born, Melbourne-based, writing independently about tea for readers across Australia and New Zealand.";

export default function About() {
  useCanonical("/about");
  usePageMeta({
    title: ABOUT_TITLE,
    description: ABOUT_DESCRIPTION,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      url: `${SITE}/about`,
      name: ABOUT_TITLE,
      description: ABOUT_DESCRIPTION,
      about: { "@id": "https://thetealist.com/#org" },
      isPartOf: { "@id": "https://thetealist.com/#website" },
      inLanguage: "en-AU",
    },
  });
  return (
    <div className="relative z-10" data-testid="about-page">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-7">
            <p className="overline mb-4">About The Tea List</p>
            <h1 className="headline-xl text-5xl md:text-7xl">
              There is more in <span className="italic text-[#1e4a35]">a cup of tea</span> than most people ever stop to consider.
            </h1>
            <div className="mt-10 prose-article max-w-xl">
              <p>
                The Tea List is an independent journal dedicated to tea —
                its leaves, its histories, its rituals, and the complex,
                often overlooked economics of one of the world's most
                consumed beverages.
              </p>
              <p>
                We are not sommeliers, nor are we affiliated with any tea
                estate, tea importer, or retailer. We are a small family
                from Sri Lanka — one of the world's foremost tea-producing
                nations and the birthplace of Ceylon tea — now based in
                Melbourne, Australia. Tea is not simply something we drink.
                It is where we come from.
              </p>
              <p>
                In a media landscape saturated with noise, we saw a need
                for something more considered: a publication that covers
                specialty tea, tea culture, and the global tea industry
                with the depth and seriousness they deserve. So we built one.
              </p>
              <p>
                Tea is far more than a beverage. From single-origin teas
                and artisan tea blends to ancient tea ceremonies and the
                livelihoods of tea farmers across Asia and Africa, tea is
                a living culture woven into the rituals, traditions, and
                identities of communities on every continent. To write
                about tea honestly is to engage with all of that — the
                tea leaf, the land, and the people behind every cup.
              </p>
              <p>
                That is what The Tea List is here to do.
              </p>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="aspect-square overflow-hidden rounded-card bg-[#f1eadb]">
              <img
                src="https://customer-assets.emergentagent.com/job_tea-time-site-1/artifacts/54qoc2gf_About_The_Tea_List.png"
                alt="A cup of tea surrounded by small bowls of loose-leaf tea, resting on a vintage world map"
                className="w-full h-full object-cover"
              />
            </div>
            <a
              href="mailto:info@thetealist.com"
              className="mt-4 inline-block font-mono text-[10px] tracking-widest text-[#1e4a35] hover:text-[#1e4a35] transition-colors"
              data-testid="about-author-email"
            >
              info@thetealist.com
            </a>
          </div>
        </div>
      </section>

      <section
        className="py-12 md:py-16 px-6 md:px-12 border-y"
        style={{ backgroundColor: "var(--bg-alt)", borderColor: "var(--border)" }}
      >
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            { k: "01", h: "Honest by default", p: "When something is sponsored, we say so. Editorial views and recommendations are always our own." },
            { k: "02", h: "Slow over fast", p: "A good review takes a month of drinking. A guide takes dozens of test batches." },
            { k: "03", h: "Reader-supported", p: "The newsletter is free. If the work helps you, share it with another tea-drinker." },
          ].map((block) => (
            <div key={block.k}>
              <p className="font-mono text-xs tracking-widest text-[#1e4a35]">{block.k}</p>
              <h3 className="mt-4 font-serif text-2xl font-medium">{block.h}</h3>
              <p className="mt-3 text-[#404e48] font-light leading-relaxed">{block.p}</p>
            </div>
          ))}
        </div>
      </section>

      <Newsletter />
    </div>
  );
}
